import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { get } from "../api";
import type { CustomerRow } from "../api";
import { readCover, writeCover } from "../customerFilter";
import type { CoverFilter } from "../customerFilter";

/**
 * The other customers, beside the one being looked at.
 *
 * The same list the customers page shows, in the same order: it is the
 * same scoped `/api/customers` call, so a CSM sees exactly their own
 * accounts and nothing here decides who may see what. If an admin arrived
 * from a "Covered by" view, the same `?user=` narrowing applies, and it can
 * be dropped from here.
 *
 * Switching keeps the tab: somebody reading metrics who clicks the next
 * customer wants that customer's metrics, not their report. The window
 * choice carries over too; a campaign filter does not, because campaigns
 * belong to one customer.
 */
export default function CustomerSidebar({ current }: { current: string }) {
  const location = useLocation();
  const [cover, setCover] = useState<CoverFilter | null>(readCover);
  const [rows, setRows] = useState<CustomerRow[] | null>(null);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let live = true;
    const q = cover ? `?user=${encodeURIComponent(cover.user)}` : "";
    void get<CustomerRow[]>(`/api/customers${q}`)
      .then((r) => live && setRows(r))
      .catch(() => live && setRows([]));
    return () => {
      live = false;
    };
  }, [cover]);

  // Close the narrow-screen drawer after navigating.
  useEffect(() => setOpen(false), [current]);

  const tab = useMemo(() => {
    const m = /^\/c\/[^/]+(\/[^/]+)?/.exec(location.pathname);
    return m?.[1] ?? "";
  }, [location.pathname]);

  const carried = useMemo(() => {
    const src = new URLSearchParams(location.search);
    const out = new URLSearchParams();
    if (tab === "/metrics") {
      for (const k of ["w", "from", "to", "d"]) {
        const v = src.get(k);
        if (v) out.set(k, v);
      }
    }
    const s = out.toString();
    return s ? `?${s}` : "";
  }, [location.search, tab]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (rows ?? []).filter(
      (c) => !q || c.display_name.toLowerCase().includes(q) || c.name.includes(q),
    );
  }, [rows, query]);

  const active = rows?.find((c) => c.name === current || c.id === current);

  return (
    <aside className={open ? "cside open" : "cside"}>
      <button className="cside-toggle" onClick={() => setOpen(!open)} aria-expanded={open}>
        {active?.display_name ?? "Customers"} ▾
      </button>

      <div className="cside-body">
        <div className="cside-head">
          <Link to="/" className="cside-all">
            All customers
          </Link>
          {rows && <span className="dim">{rows.length}</span>}
        </div>

        {cover && (
          <div className="cside-cover">
            <span>{cover.label}</span>
            <button
              className="btn tiny"
              onClick={() => {
                writeCover(null);
                setCover(null);
              }}
            >
              Show all
            </button>
          </div>
        )}

        {rows && rows.length > 8 && (
          <input
            type="search"
            className="cside-search"
            placeholder="Find a customer"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        )}

        {!rows ? (
          <div className="sub dim cside-note">Loading…</div>
        ) : (
          <nav aria-label="Customers">
            {shown.map((c) => {
              const on = c === active;
              return (
                <Link
                  key={c.id}
                  to={`/c/${c.name}${tab}${carried}`}
                  className={on ? "cside-item on" : "cside-item"}
                  aria-current={on ? "page" : undefined}
                >
                  <span className="cside-name">{c.display_name}</span>
                  {c.latest_report?.sentiment && (
                    <span
                      className={`cside-dot ${SENTIMENT_CLASS[c.latest_report.sentiment] ?? "neu"}`}
                      title={`Latest report: ${c.latest_report.sentiment}`}
                    />
                  )}
                </Link>
              );
            })}
            {shown.length === 0 && <div className="sub dim cside-note">No match.</div>}
            {/* Reachable by URL but outside the list (an admin filter that
                excludes it): still say which one is open. */}
            {rows.length > 0 && !active && (
              <div className="sub dim cside-note">The open customer is not in this list.</div>
            )}
          </nav>
        )}
      </div>
    </aside>
  );
}

const SENTIMENT_CLASS: Record<string, string> = {
  positive: "pos",
  negative: "neg",
  mixed: "mix",
  neutral: "neu",
};
